<template>
  <div class="tools-container">
    <!-- Remove Header with About button -->

    <!-- Top Configuration Panel -->
    <v-container fluid class="config-container">
      <v-card outlined class="config-panel">
          <v-card-text class="config-panel-body">
            <v-row align="center" dense class="config-row">
            <v-col cols="12" sm="6" md="auto" class="config-col config-col-compact">
              <div class="config-item">
                <label class="config-label">Country</label>
                <v-select
                  v-model="selectedCountry"
                  :items="countries"
                  item-text="name"
                  item-value="name"
                  prepend-inner-icon="mdi-earth"
                  outlined
                  dense
                  hide-details
                  class="config-select config-select-compact config-select-country"
                  :loading="isTopConfigLoading"
                  :disabled="areTopControlsDisabled"
                />
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="auto" class="config-col config-col-compact">
              <div class="config-item">
                <label class="config-label">Target Year</label>
                <v-select
                  v-model="targetYear"
                  :items="targetYears"
                  prepend-inner-icon="mdi-calendar-range"
                  outlined
                  dense
                  hide-details
                  class="config-select config-select-compact config-select-target-year"
                  :loading="isTopConfigLoading"
                  :disabled="areTopControlsDisabled"
                ></v-select>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="auto" class="config-col config-col-compact">
              <div class="config-item">
                <label class="config-label">Power Mix</label>
                <v-select
                  v-model="powerGenerationMix"
                  :items="powerGenerationMixOptions"
                  prepend-inner-icon="mdi-transmission-tower"
                  outlined
                  dense
                  hide-details
                  class="config-select config-select-compact config-select-power-mix"
                  :loading="isTopConfigLoading"
                  :disabled="areTopControlsDisabled"
                ></v-select>
              </div>
            </v-col>

            <v-col cols="12" sm="6" md="auto" class="config-col config-col-compact">
              <div class="config-item config-item-fcev">
                <div class="config-label-row config-fcev-label-row">
                  <v-checkbox
                    v-model="includeFcev"
                    label="Include FCEV"
                    dense
                    hide-details
                    class="config-checkbox config-checkbox-fcev"
                    :disabled="areTopControlsDisabled"
                  ></v-checkbox>
                </div>
                <v-select
                  v-if="includeFcev"
                  v-model="hydrogenShare"
                  :items="displayHydrogenShareOptions"
                  item-text="text"
                  item-value="value"
                  prepend-inner-icon="mdi-hydrogen-station"
                  outlined
                  dense
                  hide-details
                  class="config-select config-select-compact config-select-hydrogen-share"
                  :loading="isTopConfigLoading"
                  :disabled="areTopControlsDisabled"
                ></v-select>
              </div>
            </v-col>

          </v-row>
          
        </v-card-text>
      </v-card>
    </v-container>

    <!-- About Dialog -->
    <v-dialog v-model="aboutDialog" max-width="800" :overlay-opacity="0.7">
      <v-card>
        <v-card-title class="headline primary--text">
          <v-icon left color="primary">mdi-information</v-icon>
          Zero-Emission Vehicle Scoping Tool
        </v-card-title>
        <v-card-text class="pa-6">
          <p class="mb-4">
            The Zero-Emission Vehicle Scoping Tool (EVST) quantifies economic
            costs and benefits of alternative EV adoption scenarios at country
            level. This model covers six vehicle segments (cars, 2-wheelers,
            3-wheelers, buses, light commercial vehicles, and heavy-duty
            vehicles) and provides economic and financial analysis related to
            vehicle capital costs, fuel and maintenance costs, charging
            infrastructure cost, and local and global environmental cost, at the
            country level up to 2035. This scoping tool is developed by the Bank
            based on country specific and global data, together with the
            meticulous methodologies.
          </p>

          <p class="mb-4">
            This tool can provide a quantitative analysis of the benefits and
            costs associated with different EV adoption scenario, for targeted
            vehicle segment, which could help countries identify the most
            promising entry market segments and the economic and fiscal impact
            of the investment over time and shed lights on policy and investment
            that may be needed to support the transition. It also supports
            analysis from the users' perspective on the total cost of owning and
            using a vehicle over its lifetime.
          </p>

          <p class="mb-4">
            This scoping tool supports government agencies to identify policy
            levers that can help addressing the barriers to EV adoption.
          </p>

          <v-divider class="my-4"></v-divider>

          <h3 class="mb-3">Glossary</h3>
          <div class="glossary-section">
            <div class="glossary-item mb-2">
              <strong>EV:</strong> Electric Vehicle including BEV and FCEV
            </div>
            <div class="glossary-item mb-2">
              <strong>ICEV:</strong> Internal Combustion Engine Vehicle
            </div>
            <div class="glossary-item mb-2">
              <strong>BEV:</strong> Battery Electric Vehicle
            </div>
            <div class="glossary-item mb-2">
              <strong>FCEV:</strong> Fuel Cell Electric Vehicle
            </div>
            <div class="glossary-item mb-2">
              <strong>LCV:</strong> Light Commercial Vehicle
            </div>
            <div class="glossary-item mb-2">
              <strong>HDV:</strong> Heavy-Duty Vehicle
            </div>
            <div class="glossary-item mb-2">
              <strong>2W:</strong> Two-Wheeler
            </div>
            <div class="glossary-item mb-2">
              <strong>3W:</strong> Three-Wheeler
            </div>
          </div>

          <v-divider class="my-4"></v-divider>

          <p class="mt-4"><strong>Version:</strong> 2.0.1</p>
          <p>
            <strong>Contact:</strong>
            <a href="mailto:wqiao@worldbank.org" class="primary--text"
              >wqiao@worldbank.org</a
            >
          </p>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="aboutDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="analysisProgress.visible"
      persistent
      max-width="560"
      content-class="analysis-progress-dialog"
    >
      <v-card class="analysis-progress-card">
        <v-card-title class="analysis-progress-title">
          <v-icon left :color="analysisProgress.status === 'error' ? 'error' : analysisProgress.status === 'success' ? 'success' : 'primary'">
            {{ analysisProgress.status === 'error' ? 'mdi-alert-circle-outline' : analysisProgress.status === 'success' ? 'mdi-check-circle-outline' : 'mdi-progress-clock' }}
          </v-icon>
          {{ analysisProgressTitle }}
        </v-card-title>
        <v-card-text class="analysis-progress-body">
          <div class="analysis-progress-meta">
            <div>
              <div class="analysis-progress-kicker">
                {{ isRunningAnalysis ? 'Please keep this page open' : 'Run status' }}
              </div>
              <div class="analysis-progress-country">
                {{ getCountryName(selectedCountry) }} - {{ targetYear }} - {{ hydrogenShare }}
              </div>
            </div>
            <div class="analysis-progress-elapsed" v-if="analysisProgress.status !== 'idle'">
              {{ formatAnalysisElapsed() }}
            </div>
          </div>

          <v-alert
            v-if="isRunningAnalysis"
            dense
            outlined
            type="info"
            class="analysis-progress-alert"
          >
            We will warn you before this page closes while the Excel model is still running.
          </v-alert>

          <v-alert
            v-else-if="analysisProgress.status === 'success'"
            dense
            outlined
            type="success"
            class="analysis-progress-alert"
          >
            {{ analysisProgress.message }}
          </v-alert>

          <v-alert
            v-else-if="analysisProgress.status === 'error'"
            dense
            outlined
            type="error"
            class="analysis-progress-alert"
          >
            {{ analysisProgress.message }}
          </v-alert>

          <div class="analysis-progress-steps" v-if="analysisProgress.status === 'running'">
            <div
              v-for="(step, index) in analysisProgress.steps"
              :key="step.key"
              class="analysis-progress-step"
              :class="analysisProgressStepClass(index)"
            >
              <div class="analysis-progress-step-icon">
                <v-icon v-if="index < analysisProgress.currentStepIndex" color="success">mdi-check-circle</v-icon>
                <v-progress-circular
                  v-else-if="index === analysisProgress.currentStepIndex"
                  indeterminate
                  size="18"
                  width="2"
                  color="primary"
                />
                <v-icon v-else color="#b0bec5">mdi-circle-outline</v-icon>
              </div>
              <div class="analysis-progress-step-copy">
                <div class="analysis-progress-step-title">{{ step.title }}</div>
                <div class="analysis-progress-step-note">{{ step.note }}</div>
              </div>
            </div>
          </div>

          <div v-if="analysisProgress.detail" class="analysis-progress-detail">
            {{ analysisProgress.detail }}
          </div>
        </v-card-text>
        <v-card-actions class="analysis-progress-actions">
          <v-spacer></v-spacer>
          <v-btn
            v-if="!isRunningAnalysis"
            color="primary"
            @click="closeAnalysisProgress"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="outputReadProgress.visible"
      persistent
      max-width="440"
      content-class="analysis-progress-dialog"
    >
      <v-card class="analysis-progress-card">
        <v-card-title class="analysis-progress-title">
          <v-icon left color="primary">mdi-file-chart-outline</v-icon>
          Reading Output
        </v-card-title>
        <v-card-text class="analysis-progress-body">
          <div class="analysis-progress-country">
            {{ outputReadProgress.sectionTitle || 'Loading output data' }}
          </div>
          <div class="analysis-progress-detail">
            Reading workbook: {{ outputReadProgress.fileName || 'latest analysis workbook' }}
          </div>
          <div class="analysis-progress-step is-active mt-4">
            <div class="analysis-progress-step-icon">
              <v-progress-circular indeterminate size="18" width="2" color="primary" />
            </div>
            <div class="analysis-progress-step-copy">
              <div class="analysis-progress-step-title">Loading chart and table data</div>
              <div class="analysis-progress-step-note">This only appears while the workbook is being read for the first time.</div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="greenHydrogenModal.visible"
      max-width="460"
      persistent
    >
      <v-card>
        <v-card-title class="headline">
          <v-icon left color="green">mdi-leaf-circle</v-icon>
          Select Green Hydrogen Share
        </v-card-title>
        <v-card-text>
          <p class="mb-4">
            Hydrogen is enabled for this scenario. Please choose the green hydrogen share before continuing.
          </p>
          <v-select
            v-model="greenHydrogenModal.value"
            :items="greenHydrogenShareOptions"
            label="Green Hydrogen Share"
            outlined
            dense
            hide-details
            :menu-props="{ contentClass: 'green-hydrogen-modal-menu', offsetY: true }"
          />
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="cancelGreenHydrogenModal">Cancel</v-btn>
          <v-btn color="primary" @click="confirmGreenHydrogenModal">Apply</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row no-gutters class="tools-workspace">
      <!-- Left Navigation - Reduced width -->
      <v-col cols="2" class="tools-sidebar-col">
        <v-navigation-drawer
          permanent
          width="100%"
          class="tools-sidebar"
        >
          <div class="nav-content">
            <v-list nav dense>
              <template v-for="(section, sectionIndex) in scenarioSections">
                <v-divider v-if="sectionIndex > 0" :key="`section-divider-${sectionIndex}`" class="my-2"></v-divider>
                <div :key="`section-label-${sectionIndex}`" class="nav-section-label">
                  {{ section.title }}
                </div>
                <template v-for="item in section.items">
                  <v-divider v-if="item.dividerBefore" :key="`item-divider-${item.index}`" class="my-2"></v-divider>
                  <v-list-item
                    :key="item.index"
                    @click="setSelectedScenario(item.index)"
                    :class="{ 'v-list-item--active': selectedScenario === item.index, 'nav-item-disabled': isOutputLocked(item.index) }"
                    class="nav-item"
                  >
                    <v-list-item-icon>
                      <v-icon :color="item.color">{{ item.icon }}</v-icon>
                    </v-list-item-icon>
                    <v-list-item-content>
                      <v-list-item-title class="text-caption">{{ item.title }}</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </template>
              </template>
            </v-list>
          </div>

          <!-- About button fixed at bottom -->
          <div class="nav-bottom">
            <v-divider></v-divider>
            <v-list nav dense>
              <v-list-item @click="aboutDialog = true" class="nav-item">
                <v-list-item-icon>
                  <v-icon>mdi-information</v-icon>
                </v-list-item-icon>
                <v-list-item-content>
                  <v-list-item-title class="text-caption"
                    >About</v-list-item-title
                  >
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-navigation-drawer>
      </v-col>

      <!-- Main Content Area - Increased width -->
      <v-col cols="10" class="tools-main-col">
        <div class="main-content">
          <!-- Loading Overlay for Main Content -->
          <v-overlay
            v-if="isInputParameterLoading"
            :value="true"
            absolute
            class="input-loading-overlay"
          >
            <v-card class="input-loading-card" elevation="6">
              <v-card-title class="input-loading-title">
                <v-icon left color="primary">mdi-tune-vertical</v-icon>
                {{ inputLoadingTitle }}
              </v-card-title>
              <v-card-text class="input-loading-body">
                <div class="input-loading-detail">{{ inputLoadingDetail }}</div>
                <div class="input-loading-steps">
                  <div
                    v-for="step in inputLoadingSteps"
                    :key="step.key"
                    class="input-loading-step"
                    :class="`is-${step.state}`"
                  >
                    <div class="input-loading-step-icon">
                      <v-icon v-if="step.state === 'done'" color="success">mdi-check-circle</v-icon>
                      <v-progress-circular
                        v-else-if="step.state === 'active'"
                        indeterminate
                        size="18"
                        width="2"
                        color="primary"
                      />
                      <v-icon v-else color="#b0bec5">mdi-circle-outline</v-icon>
                    </div>
                    <div class="input-loading-step-copy">
                      <div class="input-loading-step-title">{{ step.title }}</div>
                      <div class="input-loading-step-note">{{ step.note }}</div>
                    </div>
                  </div>
                </div>
              </v-card-text>
            </v-card>
          </v-overlay>

          <!-- Scenario Settings -->
          <v-card class="scenario-settings" height="100%">
            <!-- ZEV Percentage Content -->
            <div v-if="selectedScenario === 0" class="zev-content pt-4 px-4">
              <div class="d-flex justify-end mb-4">
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="mb-2"
                  :disabled="isLoadingData"
                  @click="resetEvPercentageDefaults"
                >
                  <v-icon left small>mdi-restore</v-icon>
                  Reset to Default
                </v-btn>
              </div>
              <div class="vehicle-sliders">
                <v-row class="vehicle-row">
                  <v-col
                    cols="4"
                    v-for="vehicle in scenarioVehicleTypes"
                    :key="vehicle.value"
                    class="vehicle-slider-column"
                  >
                    <v-card outlined class="vehicle-card" elevation="2">
                      <v-card-text class="pa-4">
                        <div class="vehicle-slider-section">
                          <h4 class="vehicle-title mb-3">
                            <v-icon
                              left
                              :color="getVehicleColor(vehicle.value)"
                              >{{ vehicle.icon }}</v-icon
                            >
                            {{ vehicle.text }}
                            <v-tooltip v-if="showParameterSourceTooltips" top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  small
                                  class="source-ref-icon"
                                  color="blue-grey lighten-1"
                                  v-bind="attrs"
                                  v-on="on"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <div class="source-ref-tooltip">
                                {{ getVehiclePercentageSourceText(vehicle.value) }}
                              </div>
                            </v-tooltip>
                            <span
                              class="slider-value-editor vehicle-title-editor"
                              :style="{ color: getVehicleColor(vehicle.value), borderColor: getVehicleColor(vehicle.value) }"
                            >
                              <input
                                class="slider-value-input"
                                :value="vehiclePercentages[vehicle.value]"
                                inputmode="numeric"
                                @keydown.enter="$event.target.blur()"
                                @blur="commitSliderValue(vehiclePercentages, vehicle.value, $event, 0, 100)"
                              />
                              <span class="slider-value-suffix">%</span>
                            </span>
                          </h4>
                          <div class="slider-container">
                            <v-slider
                              v-model="vehiclePercentages[vehicle.value]"
                              :min="0"
                              :max="100"
                              :step="1"
                              :track-color="
                                getVehicleColor(vehicle.value) + '30'
                              "
                              :track-fill-color="getVehicleColor(vehicle.value)"
                              :thumb-color="getVehicleColor(vehicle.value)"
                              class="vehicle-slider"
                              hide-details
                            >
                              <template v-slot:thumb-label="{ value }">
                                {{ value }}%
                              </template>
                            </v-slider>
                            <div class="slider-labels">
                              <span class="slider-label">0%</span>
                              <span class="slider-label">100%</span>
                            </div>
                          </div>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </div>
            </div>

            <!-- Energy & Carbon Content - Two Row Design -->
            <div
              v-if="selectedScenario === 2"
              class="energy-carbon-content pa-4"
            >
              <div class="d-flex justify-end mb-3">
                <v-btn
                  small
                  outlined
                  color="primary"
                  :disabled="isLoadingData"
                  @click="resetEnergyCarbonDefaults"
                >
                  <v-icon left small>mdi-restore</v-icon>
                  Reset to Default
                </v-btn>
              </div>
              <!-- Single Row with Two Cards Side by Side -->
              <v-row>
                <!-- Renewable Energy Section - Left Card -->
                <v-col cols="6" class="d-flex">
                  <v-card outlined elevation="2" class="flex-grow-1" style="min-height: 400px;">
                    <v-card-text class="pa-4">
                      <div class="energy-section">
                        <h4 class="section-title-compact">
                          <v-icon left color="green" small>mdi-solar-power</v-icon>
                          Renewable Energy {{ targetYear }}
                          <v-tooltip v-if="showParameterSourceTooltips" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <div class="source-ref-tooltip">
                              {{ getRenewableSectionSourceText() }}
                            </div>
                          </v-tooltip>
                          <a
                            href="https://www.un.org/en/climatechange/what-is-renewable-energy#:~:text=Renewable%20energy%20is%20energy%20derived,plentiful%20and%20all%20around%20us"
                            target="_blank"
                            class="ml-2 help-link"
                            title="Visit for more information"
                          >
                            <v-icon small color="primary" class="help-icon">
                              mdi-help-circle-outline
                            </v-icon>
                          </a>
                        </h4>

                        <div class="slider-container-compact">
                          <!-- Current Value Display -->
                          <div class="current-value-display-compact">
                            <span class="current-value-editor">
                              <input
                                class="current-value-input"
                                :value="renewablePercentage"
                                inputmode="numeric"
                                @keydown.enter="$event.target.blur()"
                                @blur="commitSliderValue(null, 'renewablePercentage', $event, 0, 100)"
                              />
                              <span class="slider-value-suffix">%</span>
                            </span>
                            <v-tooltip v-if="showParameterSourceTooltips" top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <div class="source-ref-tooltip">
                                {{ getEnergySourceText('renewable_generation_shares', 'baseline', 'Renewable generation shares / Baseline') }}
                              </div>
                            </v-tooltip>
                          </div>

                            <v-tooltip v-if="showParameterSourceTooltips" top>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <div class="source-ref-tooltip">
                                {{ getEnergySourceText('carbon_price', 'low', 'Environmental damage costs / CO2 / Low') }}
                              </div>
                            </v-tooltip>

                          <!-- Enhanced Slider with Preset Buttons -->
                          <div class="slider-with-presets-compact">
                            <!-- Preset Selection Buttons -->
                            <div class="preset-buttons-compact mb-3">
                              <v-btn-toggle
                                color="green"
                                dense
                                class="preset-toggle"
                              >
                                <v-btn
                                  v-for="(preset, index) in renewablePresets"
                                  :key="index"
                                  small
                                  @click="renewablePercentage = preset.value"
                                  :class="preset.class"
                                >
                                  <v-icon left x-small>{{ preset.icon }}</v-icon>
                                  {{ preset.label }}
                                  <span class="preset-value ml-1">({{ preset.value }}%)</span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>

                            <!-- Slider -->
                            <v-slider
                              v-model="renewablePercentage"
                              :min="0"
                              :max="100"
                              :step="1"
                              track-color="rgba(76, 175, 80, 0.2)"
                              track-fill-color="#4caf50"
                              thumb-color="#4caf50"
                              class="enhanced-slider-compact"
                              hide-details
                            >
                              <template v-slot:thumb-label="{ value }">
                                {{ value }}%
                              </template>
                            </v-slider>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Carbon Price Section - Right Card -->
                <v-col cols="6" class="d-flex">
                  <v-card outlined elevation="2" class="flex-grow-1" style="min-height: 400px;">
                    <v-card-text class="pa-4">
                      <div class="carbon-section">
                        <h4 class="section-title-compact">
                          <v-icon left color="blue-grey" small>mdi-molecule-co2</v-icon>
                          Carbon Price {{ targetYear }}
                          <v-tooltip v-if="showParameterSourceTooltips" top>
                            <template v-slot:activator="{ on, attrs }">
                              <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                mdi-information-outline
                              </v-icon>
                            </template>
                            <div class="source-ref-tooltip">
                              {{ getCarbonSectionSourceText() }}
                            </div>
                          </v-tooltip>
                          <a
                            href="https://carbonpricingdashboard.worldbank.org/what-carbon-pricing"
                            target="_blank"
                            class="ml-2 help-link"
                            title="Visit for more information"
                          >
                            <v-icon small color="primary" class="help-icon">
                              mdi-help-circle-outline
                            </v-icon>
                          </a>
                        </h4>

                        <div class="slider-container-compact">
                          <!-- Current Value Display with Unit -->
                          <div class="current-value-display-compact">
                            <span class="current-value-editor">
                              <span class="slider-value-prefix">$</span>
                              <input
                                class="current-value-input"
                                :value="carbonPrice"
                                inputmode="numeric"
                                @keydown.enter="$event.target.blur()"
                                @blur="commitSliderValue(null, 'carbonPrice', $event, 10, 150)"
                              />
                            </span>
                            <span class="value-unit">per ton CO₂</span>
                          </div>

                          <!-- Enhanced Slider with Preset Buttons -->
                          <div class="slider-with-presets-compact">
                            <!-- Preset Selection Buttons -->
                            <div class="preset-buttons-compact mb-3">
                              <v-btn-toggle
                                color="blue-grey"
                                dense
                                class="preset-toggle"
                              >
                                <v-btn
                                  v-for="(preset, index) in carbonPresets"
                                  :key="index"
                                  small
                                  @click="carbonPrice = preset.value"
                                  :class="preset.class"
                                >
                                  <v-icon left x-small>{{ preset.icon }}</v-icon>
                                  {{ preset.label }}
                                  <span class="preset-value ml-1">({{ preset.value }})</span>
                                </v-btn>
                              </v-btn-toggle>
                            </div>

                            <!-- Slider -->
                            <v-slider
                              v-model="carbonPrice"
                              :min="10"
                              :max="150"
                              :step="1"
                              track-color="rgba(96, 125, 139, 0.2)"
                              track-fill-color="#607d8b"
                              thumb-color="#607d8b"
                              class="enhanced-slider-compact"
                              hide-details
                            >
                              <template v-slot:thumb-label="{ value }">
                                ${{ value }}
                              </template>
                            </v-slider>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Tax & Duties Content - Using Vuetify Components -->
            <div v-else-if="selectedScenario === 1" class="tax-duties-content">
              <!-- Tabs for Tax Scenarios -->
              <v-tabs v-model="activeTab" background-color="grey lighten-4">
                <v-tab>Tax Scenario</v-tab>
                <v-tab>Import Duty</v-tab>
                <v-tab>Subsidy</v-tab>

                <!-- Move alert to tab bar area -->
                <v-spacer></v-spacer>
                <v-btn
                  small
                  outlined
                  color="primary"
                  class="mr-4 mt-4"
                  :disabled="isLoadingData"
                  @click="resetActiveTaxTabDefaults"
                >
                  <v-icon left small>mdi-restore</v-icon>
                  Reset to Default
                </v-btn>
              </v-tabs>

              <!-- Tab Content -->
              <v-tabs-items v-model="activeTab">
                <!-- Tax Scenario Tab -->
                <v-tab-item>
                  <v-container fluid class="pa-2">
                    <!-- Tax Table using v-simple-table -->
                    <v-simple-table
                      class="tax-table-component bordered-table compact-table"
                    >
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left category-column"></th>
                            <th
                              v-for="vehicle in vehicleTypes"
                              :key="vehicle.value"
                              class="text-center vehicle-column"
                            >
                              {{ vehicle.text }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="fuel in taxPolicyRows"
                            :key="'tax-' + fuel.key"
                            class="slider-row-height"
                          >
                            <td class="category-cell">
                              <v-chip
                                small
                                :color="fuel.color"
                                text-color="white"
                                class="category-chip"
                              >
                                <v-icon left small>{{ fuel.icon }}</v-icon>
                                {{ fuel.label }}
                              </v-chip>
                            </td>
                            <td
                              v-for="vehicle in vehicleTypes"
                              :key="'tax-' + fuel.key + '-' + vehicle.value"
                              class="text-center slider-cell-td"
                            >
                              <div class="d-flex flex-column align-center slider-cell-content">
                                <span class="slider-value-editor value-input-chip mb-1" :style="getPolicyInputStyle(fuel.color)">
                                  <input
                                    class="slider-value-input"
                                    :value="taxRates[fuel.key][vehicle.value]"
                                    inputmode="numeric"
                                    @keydown.enter="$event.target.blur()"
                                    @blur="commitSliderValue(taxRates[fuel.key], vehicle.value, $event, 0, getTaxRateMax(vehicle.value))"
                                  />
                                  <span class="slider-value-suffix">%</span>
                                </span>
                                <v-tooltip v-if="showParameterSourceTooltips" top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                      mdi-information-outline
                                    </v-icon>
                                  </template>
                                  <div class="source-ref-tooltip">
                                    {{ getPolicySourceText('sales_tax', fuel.key, vehicle.value) }}
                                  </div>
                                </v-tooltip>
                                <div class="slider-wrapper">
                                  <span class="range-label range-max">{{ vehicle.value === 'lcv' ? '300%' : '250%' }}</span>
                                  <v-slider
                                    v-model="taxRates[fuel.key][vehicle.value]"
                                    :min="0"
                                    :max="vehicle.value === 'lcv' ? 300 : 250"
                                    :step="1"
                                    vertical
                                    :color="fuel.color"
                                    :track-color="fuel.trackColor"
                                    :thumb-color="fuel.color"
                                    hide-details
                                    class="vertical-slider"
                                  ></v-slider>
                                  <span class="range-label range-min">0%</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-container>
                </v-tab-item>

                <!-- Import Duty Tab -->
                <v-tab-item>
                  <v-container fluid class="pa-2">
                    <!-- Import Duty Table -->
                    <v-simple-table
                      class="tax-table-component bordered-table compact-table"
                    >
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left category-column"></th>
                            <th
                              v-for="vehicle in vehicleTypes"
                              :key="vehicle.value"
                              class="text-center vehicle-column"
                            >
                              {{ vehicle.text }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            v-for="fuel in taxPolicyRows"
                            :key="'duty-' + fuel.key"
                            class="slider-row-height"
                          >
                            <td class="category-cell">
                              <v-chip
                                small
                                :color="fuel.color"
                                text-color="white"
                                class="category-chip"
                              >
                                <v-icon left small>{{ fuel.icon }}</v-icon>
                                {{ fuel.label }}
                              </v-chip>
                            </td>
                            <td
                              v-for="vehicle in vehicleTypes"
                              :key="'duty-' + fuel.key + '-' + vehicle.value"
                              class="text-center slider-cell-td"
                            >
                              <div class="d-flex flex-column align-center slider-cell-content">
                                <span class="slider-value-editor value-input-chip mb-1" :style="getPolicyInputStyle(fuel.color)">
                                  <input
                                    class="slider-value-input"
                                    :value="importDutyRates[fuel.key][vehicle.value]"
                                    inputmode="numeric"
                                    @keydown.enter="$event.target.blur()"
                                    @blur="commitSliderValue(importDutyRates[fuel.key], vehicle.value, $event, 0, 250)"
                                  />
                                  <span class="slider-value-suffix">%</span>
                                </span>
                                <v-tooltip v-if="showParameterSourceTooltips" top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                      mdi-information-outline
                                    </v-icon>
                                  </template>
                                  <div class="source-ref-tooltip">
                                    {{ getPolicySourceText('import_duty', fuel.key, vehicle.value) }}
                                  </div>
                                </v-tooltip>
                                <div class="slider-wrapper">
                                  <span class="range-label range-max">250%</span>
                                  <v-slider
                                    v-model="importDutyRates[fuel.key][vehicle.value]"
                                    :min="0"
                                    :max="250"
                                    :step="1"
                                    vertical
                                    :color="fuel.color"
                                    :track-color="fuel.trackColor"
                                    :thumb-color="fuel.color"
                                    hide-details
                                    class="vertical-slider"
                                  ></v-slider>
                                  <span class="range-label range-min">0%</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-container>
                </v-tab-item>

                <!-- Subsidy Tab -->
                <v-tab-item>
                  <v-container fluid class="pa-2">
                    <!-- Subsidy Table -->
                    <v-simple-table
                      class="tax-table-component bordered-table compact-table"
                    >
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left category-column"></th>
                            <th
                              v-for="vehicle in vehicleTypes"
                              :key="vehicle.value"
                              class="text-center vehicle-column"
                            >
                              {{ vehicle.text }}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          <!-- BEV Row -->
                          <tr class="slider-row-height">
                            <td class="category-cell">
                              <v-chip
                                small
                                color="green"
                                text-color="white"
                                class="category-chip"
                              >
                                <v-icon left small>mdi-battery-high</v-icon>
                                BEV
                              </v-chip>
                            </td>
                            <td
                              v-for="vehicle in vehicleTypes"
                              :key="'subsidy-bev-' + vehicle.value"
                              class="text-center slider-cell-td"
                            >
                              <div
                                class="d-flex flex-column align-center slider-cell-content"
                              >
                                <span class="slider-value-editor value-input-chip money-input green-input mb-1">
                                  <span class="slider-value-prefix">$</span>
                                  <input
                                    class="slider-value-input"
                                    :value="subsidyAmounts.bev[vehicle.value]"
                                    inputmode="numeric"
                                    @keydown.enter="$event.target.blur()"
                                    @blur="commitSliderValue(subsidyAmounts.bev, vehicle.value, $event, 0, getSubsidyMax(vehicle.value))"
                                  />
                                </span>
                                <v-tooltip v-if="showParameterSourceTooltips" top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                      mdi-information-outline
                                    </v-icon>
                                  </template>
                                  <div class="source-ref-tooltip">
                                    {{ getPolicySourceText('subsidy', 'bev', vehicle.value) }}
                                  </div>
                                </v-tooltip>
                                <div class="slider-wrapper">
                                  <span class="range-label range-max"
                                    >{{ (vehicle.value === 'bus' || vehicle.value === 'hdv') ? '$1,000,000' : '$100,000' }}</span
                                  >
                                  <v-slider
                                    v-model="subsidyAmounts.bev[vehicle.value]"
                                    :min="0"
                                    :max="(vehicle.value === 'bus' || vehicle.value === 'hdv') ? 1000000 : 100000"
                                    :step="1"
                                    vertical
                                    color="green"
                                    track-color="green lighten-4"
                                    thumb-color="green"
                                    hide-details
                                    class="vertical-slider"
                                  ></v-slider>
                                  <span class="range-label range-min">$0</span>
                                </div>
                              </div>
                            </td>
                          </tr>

                          <!-- FCEV Row -->
                          <tr class="slider-row-height">
                            <td class="category-cell">
                              <v-chip
                                small
                                color="purple"
                                text-color="white"
                                class="category-chip"
                              >
                                <v-icon left small>mdi-hydrogen-station</v-icon>
                                Hydrogen
                              </v-chip>
                            </td>
                            <td
                              v-for="vehicle in vehicleTypes"
                              :key="'subsidy-hydrogen-' + vehicle.value"
                              class="text-center slider-cell-td"
                            >
                              <div
                                class="d-flex flex-column align-center slider-cell-content"
                              >
                                <span class="slider-value-editor value-input-chip money-input purple-input mb-1">
                                  <span class="slider-value-prefix">$</span>
                                  <input
                                    class="slider-value-input"
                                    :value="subsidyAmounts.hydrogen[vehicle.value]"
                                    inputmode="numeric"
                                    @keydown.enter="$event.target.blur()"
                                    @blur="commitSliderValue(subsidyAmounts.hydrogen, vehicle.value, $event, 0, getSubsidyMax(vehicle.value))"
                                  />
                                </span>
                                <v-tooltip v-if="showParameterSourceTooltips" top>
                                  <template v-slot:activator="{ on, attrs }">
                                    <v-icon small class="source-ref-icon" color="blue-grey lighten-1" v-bind="attrs" v-on="on">
                                      mdi-information-outline
                                    </v-icon>
                                  </template>
                                  <div class="source-ref-tooltip">
                                    {{ getPolicySourceText('subsidy', 'hydrogen', vehicle.value) }}
                                  </div>
                                </v-tooltip>
                                <div class="slider-wrapper">
                                  <span class="range-label range-max"
                                    >{{ (vehicle.value === 'bus' || vehicle.value === 'hdv') ? '$1,000,000' : '$100,000' }}</span
                                  >
                                  <v-slider
                                    v-model="subsidyAmounts.hydrogen[vehicle.value]"
                                    :min="0"
                                    :max="(vehicle.value === 'bus' || vehicle.value === 'hdv') ? 1000000 : 100000"
                                    :step="1"
                                    vertical
                                    color="purple"
                                    track-color="purple lighten-4"
                                    thumb-color="purple"
                                    hide-details
                                    class="vertical-slider"
                                  ></v-slider>
                                  <span class="range-label range-min">$0</span>
                                </div>
                              </div>
                            </td>
                          </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-container>
                </v-tab-item>
              </v-tabs-items>
            </div>

            <!-- Summary & Review Content -->
            <div v-else-if="selectedScenario === 3" class="summary-content pa-4">
              <!-- Summary Header -->
              <div class="summary-header mb-4">
                <v-row align="center">
                  <v-col cols="auto">
                    <div class="country-info d-flex align-center">
                      <span
                        v-if="getCountryCode(selectedCountry)"
                        class="flag-icon mr-3"
                        :class="`flag-icon-${getCountryCode(selectedCountry).toLowerCase()}`"
                      ></span>
                      <div>
                        <div class="d-flex align-center">
                          <h2 class="country-name mb-0">{{ getCountryName(selectedCountry) }}</h2>
                          <p class="country-details mb-0 text--secondary" style="margin-left:12px;">
                            Target Year: {{ targetYear }} | Hydrogen: {{ hydrogenShare }} | Power Mix: {{ powerGenerationMix }}
                          </p>
                        </div>
                      </div>
                    </div>
                  </v-col>
                  <v-spacer></v-spacer>
                  
                </v-row>
              </div>

              <!-- Summary Cards - First Row: EV and Environment -->
              <v-row class="summary-cards-top mb-3">
                <!-- EV Adoption Card -->
                <v-col cols="6" class="pa-2 d-flex">
                  <v-card class="summary-card  flex-grow-1" elevation="3">
                    <v-card-title class="card-header zev-header pa-3">
                      <v-icon left color="white" small>mdi-car-electric</v-icon>
                      <span class="card-title-text">EV Targets</span>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small color="white" @click="setSelectedScenario(0)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-2 card-content-scrollable">
                      <div class="zev-summary-scrollable">                        
                        <!-- Vehicle Percentages Grid - 3 Cards Per Row -->
                        <div class="vehicle-grid-three-per-row">
                          <div 
                            v-for="vehicle in scenarioVehicleTypes" 
                            :key="vehicle.value"
                            class="vehicle-subcard"
                          >
                            <div class="vehicle-subcard-header">
                              <v-icon x-small :color="getVehicleColor(vehicle.value)" class="mr-1">
                                {{ vehicle.icon }}
                              </v-icon>
                              <span class="vehicle-name-subcard">{{ vehicle.text }}</span>
                            </div>
                            <div class="vehicle-percentage-display">
                              <span class="percentage-main">{{ vehiclePercentages[vehicle.value] }}%</span>
                            </div>
                            <v-progress-linear
                              :value="vehiclePercentages[vehicle.value]"
                              :color="getVehicleColor(vehicle.value)"
                              height="4"
                              rounded
                            ></v-progress-linear>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>

                <!-- Energy & Environment Card -->
                <v-col cols="6" class="pa-2 d-flex">
                  <v-card class="summary-card environment-card-adaptive flex-grow-1 d-flex flex-column" elevation="3">
                    <v-card-title class="card-header environment-header pa-3">
                      <v-icon left color="white" small>mdi-leaf</v-icon>
                      <span class="card-title-text">Energy & Environment</span>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small color="white" @click="setSelectedScenario(2)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-2 card-content-scrollable flex-grow-1">
                      <div class="environment-summary-scrollable" style="height: 100%;">
                        <!-- Environment Sub-cards Grid - 2 Cards Per Row -->
                        <div class="environment-grid-two-per-row" style="height: 100%;">
                          <!-- Renewable Energy Sub-card -->
                          <div class="env-subcard renewable-energy-card">
                            <div class="env-subcard-header">
                              <v-icon x-small color="green" class="mr-1">mdi-solar-power</v-icon>
                              <span class="env-name-subcard">Renewable Energy</span>
                            </div>
                            <div class="env-percentage-display">
                              <span class="env-percentage-main" style="color: #4caf50; padding: 0 10px">{{ renewablePercentage }}%</span>
                              <span class="env-percentage-label">{{ getRenewableLabel() }}</span>
                            </div>
                            <v-progress-linear
                              :value="renewablePercentage"
                              color="green"
                              height="6"
                              rounded
                              class="env-progress-subcard"
                              background-color="#f5f5f5"
                            ></v-progress-linear>
                            <div class="env-subcard-footer">
                              <span class="env-footer-text">by {{ targetYear }}</span>
                            </div>
                          </div>

                          <!-- Carbon Price Sub-card -->
                          <div class="env-subcard carbon-price-card">
                            <div class="env-subcard-header">
                              <v-icon x-small color="blue-grey" class="mr-1">mdi-molecule-co2</v-icon>
                              <span class="env-name-subcard">Carbon Price</span>
                            </div>
                            <div class="env-percentage-display">
                              <span class="env-percentage-main" style="color: #607d8b; padding: 0 10px">${{ carbonPrice }}</span>
                              <span class="env-percentage-label">{{ getCarbonPriceLabel() }}</span>
                            </div>
                            <div class="carbon-price-visual">
                              <v-chip
                                x-small
                                :color="getCarbonPriceColor()"
                                text-color="white"
                                class="carbon-level-chip"
                              >
                                {{ getCarbonPriceLabel() }} Level
                              </v-chip>
                            </div>
                            <div class="env-subcard-footer">
                              <span class="env-footer-text">per ton CO₂</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Summary Cards - Second Row: Financial Policies -->
              <v-row class="summary-cards-bottom mb-4">
                <v-col cols="12" class="pa-2">
                  <v-card class="summary-card financial-card-adaptive" elevation="3">
                    <v-card-title class="card-header financial-header pa-3">
                      <v-icon left color="white" small>mdi-cash-multiple</v-icon>
                      <span class="card-title-text">Financial Policies</span>
                      <v-spacer></v-spacer>
                      <v-btn icon x-small color="white" @click="setSelectedScenario(1)">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </v-card-title>
                    <v-card-text class="pa-2 card-content-adaptive">
                      <!-- Compact Financial Policies Table -->
                      <div class="financial-table-container">
                        <v-simple-table class="financial-summary-table-adaptive" dense>
                          <template v-slot:default>
                            <thead>
                              <tr>
                                <th class="policy-type-header">Policy Type</th>
                                <th class="vehicle-type-header">Vehicle Type</th>
                                <th
                                  v-for="vehicle in vehicleTypes"
                                  :key="vehicle.value"
                                  class="vehicle-header-cell"
                                >
                                  <div class="vehicle-header-content">
                                    <v-icon small :color="getVehicleColor(vehicle.value)">
                                      {{ vehicle.icon }}
                                    </v-icon>
                                    <span class="vehicle-name-compact">{{ vehicle.text }}</span>
                                  </div>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              <!-- Tax Rates -->
                              <tr
                                v-for="(fuel, fuelIndex) in taxPolicyRows"
                                :key="'summary-tax-' + fuel.key"
                                class="policy-row"
                              >
                                <td v-if="fuelIndex === 0" :rowspan="taxPolicyRows.length" class="policy-category">
                                  <div class="policy-category-content">
                                    <v-icon small color="blue" class="mr-1">mdi-percent</v-icon>
                                    <span class="policy-name">Tax Rates</span>
                                  </div>
                                </td>
                                <td class="vehicle-type-cell">
                                  <v-chip x-small :color="fuel.color" text-color="white">{{ fuel.label }}</v-chip>
                                </td>
                                <td
                                  v-for="vehicle in vehicleTypes"
                                  :key="'summary-tax-' + fuel.key + '-' + vehicle.value"
                                  class="value-cell"
                                >
                                  <span class="value-text">{{ taxRates[fuel.key][vehicle.value] }}%</span>
                                </td>
                              </tr>

                              <!-- Import Duties -->
                              <tr
                                v-for="(fuel, fuelIndex) in taxPolicyRows"
                                :key="'summary-duty-' + fuel.key"
                                class="policy-row"
                              >
                                <td v-if="fuelIndex === 0" :rowspan="taxPolicyRows.length" class="policy-category">
                                  <div class="policy-category-content">
                                    <v-icon small color="orange" class="mr-1">mdi-import</v-icon>
                                    <span class="policy-name">Import Duties</span>
                                  </div>
                                </td>
                                <td class="vehicle-type-cell">
                                  <v-chip x-small :color="fuel.color" text-color="white">{{ fuel.label }}</v-chip>
                                </td>
                                <td
                                  v-for="vehicle in vehicleTypes"
                                  :key="'summary-duty-' + fuel.key + '-' + vehicle.value"
                                  class="value-cell"
                                >
                                  <span class="value-text">{{ importDutyRates[fuel.key][vehicle.value] }}%</span>
                                </td>
                              </tr>

                              <!-- Subsidies -->
                              <tr class="policy-row">
                                <td rowspan="2" class="policy-category">
                                  <div class="policy-category-content">
                                    <v-icon small color="purple" class="mr-1">mdi-cash-plus</v-icon>
                                    <span class="policy-name">Subsidies</span>
                                  </div>
                                </td>
                                <td class="vehicle-type-cell">
                                  <v-chip x-small color="green" text-color="white">BEV</v-chip>
                                </td>
                                <td
                                  v-for="vehicle in vehicleTypes"
                                  :key="'subsidy-bev-' + vehicle.value"
                                  class="value-cell"
                                >
                                  <span class="value-text subsidy-text">${{ subsidyAmounts.bev[vehicle.value].toLocaleString() }}</span>
                                </td>
                              </tr>
                              <tr class="policy-row">
                                <td class="vehicle-type-cell">
                                  <v-chip x-small color="teal" text-color="white">Hydrogen</v-chip>
                                </td>
                                <td
                                  v-for="vehicle in vehicleTypes"
                                  :key="'subsidy-hydrogen-' + vehicle.value"
                                  class="value-cell"
                                >
                                  <span class="value-text subsidy-text">${{ subsidyAmounts.hydrogen[vehicle.value].toLocaleString() }}</span>
                                </td>
                              </tr>
                            </tbody>
                          </template>
                        </v-simple-table>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <!-- Action Panel -->
              <v-row>
                <v-col cols="12" class="pa-2">
                  <v-card class="action-panel-adaptive" elevation="2">
                    <!-- Action buttons and summary download -->
                    <v-card-text class="d-flex justify-end pa-3">
                      <v-btn
                        color="primary"
                        class="mr-2"
                        @click="runAnalysis"
                        :disabled="!isAnalysisReady || isRunningAnalysis"
                        :loading="isRunningAnalysis"
                      >
                        <v-icon left>mdi-play</v-icon>
                        Run Analysis
                      </v-btn>
                        <v-btn
                          color="secondary"
                          @click="downloadResult"
                          :disabled="!isResultDownloadAvailable"
                        >
                          <v-icon left>mdi-download</v-icon>
                          Download Result
                        </v-btn>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <FactChartTab
              v-else-if="selectedScenario === 4"
              :domain="$domain"
              :analysis-result="analysisResult"
              :selected-country-name="getCountryName(selectedCountry)"
              @loading-state="handleOutputLoadingState"
              @file-read="handleOutputFileRead"
            />

            <TcoGraphTab
              v-else-if="selectedScenario === 5"
              :domain="$domain"
              :analysis-result="analysisResult"
              @loading-state="handleOutputLoadingState"
              @file-read="handleOutputFileRead"
            />

            <EnergyChartTab
              v-else-if="selectedScenario === 6"
              :domain="$domain"
              :analysis-result="analysisResult"
              @loading-state="handleOutputLoadingState"
              @file-read="handleOutputFileRead"
            />

            <ExcelGraphTab
              v-else-if="selectedScenario === 7"
              :domain="$domain"
              :analysis-result="analysisResult"
              @loading-state="handleOutputLoadingState"
              @file-read="handleOutputFileRead"
            />

            <!-- Other Visualization Content -->
            <div v-else-if="selectedScenario >= 8" class="visualization-content pa-4">
              <v-card outlined elevation="2" class="visualization-panel">
                <v-card-title class="visualization-title">
                  <v-icon left :color="scenarioItems[selectedScenario].color">{{ scenarioItems[selectedScenario].icon }}</v-icon>
                  {{ scenarioItems[selectedScenario].title }}
                </v-card-title>
              </v-card>
            </div>
          </v-card>
        </div>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import FactChartTab from './tools/FactChartTab.vue'
import TcoGraphTab from './tools/TcoGraphTab.vue'
import EnergyChartTab from './tools/EnergyChartTab.vue'
import ExcelGraphTab from './tools/ExcelGraphTab.vue'
import { getVehicleTypeColor } from './tools/colorPalette'
import { SHOW_PARAMETER_SOURCE_TOOLTIPS } from './tools/sourceTooltipConfig'

const ALLOW_OUTPUTS_WITHOUT_ANALYSIS = true
const DEMO_ANALYSIS_STEP_DELAY_MS = 600
const COUNTRY_CODE_BY_NAME = {
  Albania: 'AL',
  Angola: 'AO',
  Bangladesh: 'BD',
  Benin: 'BJ',
  Brazil: 'BR',
  'Burkina Faso': 'BF',
  Cambodia: 'KH',
  Chile: 'CL',
  "Cote d'Ivoire": 'CI',
  Egypt: 'EG',
  Ethiopia: 'ET',
  Ghana: 'GH',
  India: 'IN',
  Iran: 'IR',
  Jamaica: 'JM',
  Jordan: 'JO',
  Kazakhstan: 'KZ',
  Kenya: 'KE',
  Malaysia: 'MY',
  Maldives: 'MV',
  Mali: 'ML',
  Malawi: 'MW',
  Nepal: 'NP',
  Nigeria: 'NG',
  Pakistan: 'PK',
  Poland: 'PL',
  Rwanda: 'RW',
  'São Tomé and Príncipe': 'ST',
  'S茫o Tom茅 and Pr铆ncipe': 'ST',
  Senegal: 'SN',
  Serbia: 'RS',
  'Sierra Leone': 'SL',
  'South Africa': 'ZA',
  'South Korea': 'KR',
  'Sri Lanka': 'LK',
  Tajikistan: 'TJ',
  Tanzania: 'TZ',
  Turkey: 'TR',
  Uganda: 'UG',
  Ukraine: 'UA',
  Uruguay: 'UY',
  Uzbekistan: 'UZ',
  Vanuatu: 'VU',
  Vietnam: 'VN',
}
const SCENARIO_VEHICLE_DEFINITIONS = {
  Car: { text: 'Car', value: 'car', icon: 'mdi-car' },
  '2W': { text: '2-Wheeler', value: '2w', icon: 'mdi-motorbike' },
  '3W': { text: '3-Wheeler', value: '3w', icon: 'mdi-rickshaw' },
  MB: { text: 'Minibus', value: 'mb', icon: 'mdi-van-passenger' },
  Bus: { text: 'Bus', value: 'bus', icon: 'mdi-bus' },
  AB: { text: 'Articulated Bus', value: 'ab', icon: 'mdi-bus-articulated-front' },
  LCV: { text: 'LCV', value: 'lcv', icon: 'mdi-van-utility' },
  HDV: { text: 'HDV', value: 'hdv', icon: 'mdi-truck' }
}
const DEFAULT_SCENARIO_VEHICLE_HEADERS = ['Car', '2W', '3W', 'MB', 'Bus', 'AB', 'LCV', 'HDV']

export default {
  name: "Tools",
  components: {
    FactChartTab,
    TcoGraphTab,
    EnergyChartTab,
    ExcelGraphTab
  },
  data() {
    return {
      aboutDialog: false,
      selectedScenario: 0,
      activeTab: 0,
      selectedVehicleTypes: ["car", "bus"],

      // Top configuration options
      selectedCountry: null,
      targetYear: 2035,
        includeFcev: false,
        hydrogenShare: 'No Hydrogen',
        powerGenerationMix: 'CPAT Projection',
        greenHydrogenShare: '100% Green',
        greenHydrogenShareConfirmed: false,

      // Configuration options
      countries: [],
      hydrogenShareOptions: [],
      powerGenerationMixOptions: [],
      greenHydrogenShareOptions: [],
      scenarioConfigDefaults: {
        country_name: null,
        target_year_label: '45% by 2035',
        hydrogen_share: 'No Hydrogen',
        power_generation_mix: 'CPAT Projection',
        green_hydrogen_share: '100% Green',
      },
      isBootstrappingConfig: false,


      targetYears: [
        { text: 'by 2030', value: 2030 },
        { text: 'by 2035', value: 2035 },
      ],

      // Scenario navigation items
      scenarioItems: [
        { title: "EV Percentage", icon: "mdi-car-electric", color: "#2F80ED" },
        { title: "Tax/Duty/Subsidy", icon: "mdi-cash-multiple", color: "#F2A900" },
        { title: "Energy & Carbon", icon: "mdi-leaf", color: "#27AE60" },
        { title: "Summary & Review", icon: "mdi-chart-box-outline", color: "#6C5CE7", dividerBefore: true },
        { title: "Fact Chart", icon: "mdi-chart-bar", color: "#3B82F6" },
        { title: "TCO Graph", icon: "mdi-chart-line", color: "#00A6A6" },
        { title: "Energy Chart", icon: "mdi-lightning-bolt", color: "#FF8A00" },
        { title: "Excel Graph", icon: "mdi-chart-areaspline", color: "#8E44AD" },
      ],

      // Vehicle types - updated to match ZEVST terminology and required sequence
      vehicleTypes: [
        { text: "Car", value: "car", icon: "mdi-car" },
        { text: "2-Wheeler", value: "2w", icon: "mdi-motorbike" },
        { text: "3-Wheeler", value: "3w", icon: "mdi-rickshaw" },
        { text: "Bus", value: "bus", icon: "mdi-bus" },
        { text: "LCV", value: "lcv", icon: "mdi-van-utility" },
        { text: "HDV", value: "hdv", icon: "mdi-truck" },
      ],

      // Settings values
      zevPercentage: 30,
      taxRate: 15,
      importDutyRate: 10,
      subsidyAmount: 5000,

      // Vehicle percentages for ZEV sliders
      vehiclePercentages: {
        car: 50,
        "2w": 75,
        "3w": 75,
        mb: 50,
        bus: 50,
        ab: 50,
        lcv: 50,
        hdv: 50,
      },

      // Tax, Import Duty, and Subsidy values for each vehicle type
      taxRates: {
        petrol_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        diesel_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
      },

      importDutyRates: {
        petrol_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        diesel_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
      },

      subsidyAmounts: {
        bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
      },

      // Energy & Carbon settings
      renewablePercentage: 52,
      carbonPrice: 55,

      // Add new data property for ZEV scenario data
      zevScenarioData: null,
      isLoadingScenarioData: false,

      // Add new data property for ZEV master data
      zevMasterData: null,
      isLoadingMasterData: false,
      
      // Add timeout for debouncing country changes
      countryChangeTimeout: null,

      // Add new data property for analysis result
      analysisResult: null,
      
      // Add separate loading state for analysis
      isRunningAnalysis: false,
      analysisElapsedSeconds: 0,
      analysisElapsedTimer: null,
      analysisStepTimer: null,
      analysisProgress: {
        visible: false,
        status: 'idle',
        message: '',
        detail: '',
        currentStepIndex: 0,
        steps: [
          {
            key: 'prepare',
            title: 'Preparing scenario inputs',
            note: 'Packaging your country, policy, and energy settings.'
          },
          {
            key: 'update_workbook',
            title: 'Updating the Excel model file',
            note: 'Writing the selected inputs into the analysis workbook.'
          },
          {
            key: 'recalculate',
            title: 'Recalculating workbook formulas',
            note: 'Excel is refreshing the linked sheets and model formulas.'
          },
          {
            key: 'finalize',
            title: 'Finalizing analysis file',
            note: 'Saving the recalculated workbook and preparing results.'
          }
        ]
      },
      outputReadProgress: {
        visible: false,
        section: '',
        sectionTitle: '',
        fileName: ''
      },
      outputSectionsRead: {},
      greenHydrogenModal: {
        visible: false,
        value: '100% Green',
        previousHydrogenShare: 'No Hydrogen'
      },
      suppressGreenHydrogenModal: false,

    };
  },

  computed: {
    scenarioSections() {
      return [
        {
          title: 'Inputs',
          items: this.scenarioItems.slice(0, 4).map((item, index) => ({
            ...item,
            index
          }))
        },
        {
          title: 'Outputs',
          items: this.scenarioItems.slice(4).map((item, offset) => ({
            ...item,
            index: offset + 4
          }))
        }
      ];
    },

    renewablePresets() {
      // Get actual values from API data if available
      if (this.zevMasterData && this.zevMasterData.data && this.zevMasterData.data.renewable_generation_shares) {
        // Check if renewable_generation_shares exists with baseline and scenario
        const renewableData = this.zevMasterData.data;
        if (renewableData.renewable_generation_shares) {
          const baselineValue = Math.round(renewableData.renewable_generation_shares.baseline * 100);
          const scenarioValue = Math.round(renewableData.renewable_generation_shares.scenario * 100);

          return [
            { value: baselineValue, label: "Baseline", class: "preset-baseline", icon: "mdi-chart-line-variant" },
            { value: scenarioValue, label: "Scenario", class: "preset-scenario", icon: "mdi-trending-up" }
          ];
        }
        
        // Fallback to direct baseline/scenario properties if they exist
        const baselineValue = Math.round(renewableData.baseline * 100);
        const scenarioValue = Math.round(renewableData.scenario * 100);

        return [
          { value: baselineValue, label: "Baseline", class: "preset-baseline", icon: "mdi-chart-line-variant" },
          { value: scenarioValue, label: "Scenario", class: "preset-scenario", icon: "mdi-trending-up" }
        ];
      }
      
      // Fallback default values
      return [
        { value: 0, label: "Baseline", class: "preset-baseline", icon: "mdi-chart-line-variant" },
        { value: 0, label: "Scenario", class: "preset-scenario", icon: "mdi-trending-up" }
      ];
    },

    carbonPresets() {
      // Get actual values from API data if available
      if (this.zevMasterData && this.zevMasterData.data && this.zevMasterData.data.carbon_price) {
        const carbonData = this.zevMasterData.data.carbon_price;
        const lowValue = Math.round(carbonData.low);
        const highValue = Math.round(carbonData.high);

        return [
          { value: lowValue, label: "Low", class: "preset-low", icon: "mdi-arrow-down" },
          { value: highValue, label: "High", class: "preset-high", icon: "mdi-arrow-up" }
        ];
      }
      
      // Fallback default values
      return [
        { value: 0, label: "Low", class: "preset-low", icon: "mdi-arrow-down" },
        { value: 0, label: "High", class: "preset-high", icon: "mdi-arrow-up" }
      ];
    },

    isAnalysisReady() {
      // Check if scenario data is loaded and other conditions are met
      return this.zevScenarioData !== null && this.selectedCountry && this.targetYear;
    },

      isResultDownloadAvailable() {
        return !!(this.analysisResult && this.analysisResult.file_name);
      },

    isLoadingData() {
      return this.isLoadingScenarioData || this.isLoadingMasterData || this.isRunningAnalysis;
    },

    isInputParameterLoading() {
      return this.isTopConfigLoading || this.isLoadingScenarioData || this.isLoadingMasterData;
    },

    isTopConfigLoading() {
      return this.isBootstrappingConfig;
    },

    areTopControlsDisabled() {
      return this.isBootstrappingConfig || this.isLoadingScenarioData || this.isLoadingMasterData || this.isRunningAnalysis;
    },

    canAccessOutputs() {
      return ALLOW_OUTPUTS_WITHOUT_ANALYSIS || !!this.analysisResult;
    },

    showParameterSourceTooltips() {
      return SHOW_PARAMETER_SOURCE_TOOLTIPS;
    },

    showGreenHydrogenShare() {
        return this.hydrogenShare && this.hydrogenShare !== 'No Hydrogen';
      },

      showConfirmedGreenHydrogenShare() {
        return this.showGreenHydrogenShare && this.greenHydrogenShareConfirmed && !!this.greenHydrogenShare
      },

      displayHydrogenShareOptions() {
        return (this.hydrogenShareOptions || []).map((option) => {
          const baseText = typeof option === 'object' && option !== null
            ? option.text
            : option;
          const value = typeof option === 'object' && option !== null
            ? option.value
            : option;
          const shouldShowGreenShare = this.showConfirmedGreenHydrogenShare && value === this.hydrogenShare;

          return {
            text: shouldShowGreenShare ? `${baseText} (${this.greenHydrogenShare})` : baseText,
            value,
          };
        });
      },

    scenarioVehicleTypes() {
      const headers = this.zevScenarioData && Array.isArray(this.zevScenarioData.vehicle_headers) && this.zevScenarioData.vehicle_headers.length
        ? this.zevScenarioData.vehicle_headers
        : DEFAULT_SCENARIO_VEHICLE_HEADERS;

      return headers.map((header) => {
        const definition = SCENARIO_VEHICLE_DEFINITIONS[header];
        if (definition) return definition;
        const normalizedValue = String(header).trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
        return {
          text: String(header).trim(),
          value: normalizedValue,
          icon: 'mdi-shape-outline'
        };
      });
    },

    taxPolicyRows() {
      return [
        { key: 'petrol_ice', label: 'Petrol ICE', color: '#2F80ED', trackColor: 'blue lighten-4', icon: 'mdi-fuel' },
        { key: 'diesel_ice', label: 'Diesel ICE', color: '#5B6472', trackColor: 'blue-grey lighten-4', icon: 'mdi-truck' },
        { key: 'bev', label: 'BEV', color: '#27AE60', trackColor: 'green lighten-4', icon: 'mdi-battery-high' },
        { key: 'hydrogen', label: 'Hydrogen', color: '#00A6A6', trackColor: 'teal lighten-4', icon: 'mdi-hydrogen-station' }
      ];
    },

    analysisProgressTitle() {
      if (this.analysisProgress.status === 'success') {
        return 'Analysis Complete';
      }
      if (this.analysisProgress.status === 'error') {
        return 'Analysis Failed';
      }
      return 'Running Analysis';
    },

    inputLoadingTitle() {
      if (this.isBootstrappingConfig) {
        return 'Preparing EV inputs';
      }
      if (this.isLoadingScenarioData) {
        return 'Loading EV percentages';
      }
      if (this.isLoadingMasterData) {
        return 'Loading tax, energy, and carbon settings';
      }
      return 'Preparing EV inputs';
    },

    inputLoadingDetail() {
      if (this.isBootstrappingConfig) {
        return 'Reading workbook-backed dropdown defaults and available scenario options.';
      }
      if (this.isLoadingScenarioData && this.isLoadingMasterData) {
        return 'Refreshing scenario and master parameters for the selected country and target year.';
      }
      if (this.isLoadingScenarioData) {
        return 'Fetching vehicle-share defaults from the Scenario sheet.';
      }
      if (this.isLoadingMasterData) {
        return 'Fetching policy, renewable-energy, and carbon-price inputs from the Master sheet.';
      }
      return 'Loading EV tool parameters.';
    },

    inputLoadingSteps() {
      const activeKey = this.isBootstrappingConfig
        ? 'config'
        : this.isLoadingScenarioData
          ? 'scenario'
          : this.isLoadingMasterData
            ? 'master'
            : null;

      const keys = ['config', 'scenario', 'master'];
      const activeIndex = activeKey ? keys.indexOf(activeKey) : -1;

      return [
        {
          key: 'config',
          title: 'Load configuration',
          note: 'Country list, target-year options, and scenario defaults.',
          state: activeKey === 'config' ? 'active' : activeIndex > 0 ? 'done' : 'pending'
        },
        {
          key: 'scenario',
          title: 'Load EV percentage inputs',
          note: 'Vehicle shares from the Scenario worksheet.',
          state: activeKey === 'scenario' ? 'active' : activeIndex > 1 ? 'done' : 'pending'
        },
        {
          key: 'master',
          title: 'Load policy and energy inputs',
          note: 'Tax, subsidy, import-duty, renewable, and carbon settings.',
          state: activeKey === 'master' ? 'active' : 'pending'
        }
      ];
    }
  },

  methods: {
    getCurrentTabs() {
      switch (this.selectedScenario) {
        case 0: // EV Percentage
          return ["EV Targets", "Implementation Timeline"];
        case 1: // Tax & Duties
          return ["Tax Scenario", "Import Duty", "Subsidy"];
        case 2: // Energy & Carbon
          return []; // No tabs needed for this scenario
        default:
          return ["General"];
      }
    },

    getVehicleColor(vehicleType) {
      return getVehicleTypeColor(vehicleType);
    },

    getPolicyInputStyle(color) {
      return {
        color,
        borderColor: color
      };
    },

    normalizeSourceHeader(header) {
      return String(header || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_');
    },

    getVehicleLabelByKey(vehicleKey) {
      const scenarioVehicle = this.scenarioVehicleTypes.find((vehicle) => vehicle.value === vehicleKey);
      if (scenarioVehicle) return scenarioVehicle.text;
      const taxVehicle = this.vehicleTypes.find((vehicle) => vehicle.value === vehicleKey);
      return taxVehicle ? taxVehicle.text : vehicleKey;
    },

    getFuelLabelByKey(fuelKey) {
      const taxFuel = this.taxPolicyRows.find((fuel) => fuel.key === fuelKey);
      if (taxFuel) return taxFuel.label;
      if (fuelKey === 'bev') return 'BEV';
      if (fuelKey === 'hydrogen') return 'Hydrogen';
      return fuelKey;
    },

    formatSourceTooltip(lines) {
      return lines.filter(Boolean).join('\n');
    },

    getVehiclePercentageSourceText(vehicleKey) {
      const sourceMap = this.zevScenarioData && this.zevScenarioData.source_refs
        ? this.zevScenarioData.source_refs.vehicle_percentages || {}
        : {};

      const entry = Object.entries(sourceMap).find(([header]) => {
        const definition = SCENARIO_VEHICLE_DEFINITIONS[header];
        if (definition) {
          return definition.value === vehicleKey;
        }
        return this.normalizeSourceHeader(header) === vehicleKey;
      });

      if (!entry) {
        return 'Source metadata unavailable for this Scenario-sheet value.';
      }

      const [header, ref] = entry;
      return this.formatSourceTooltip([
        `${header}: ${ref.cell}`,
        `Header: ${ref.header_cell}`,
        `Scenario row: ${ref.scenario_cell}${ref.scenario_name ? ` (${ref.scenario_name})` : ''}`,
      ]);
    },

    getPolicySourceText(sectionKey, fuelKey, vehicleKey) {
      const sourceMap = this.zevMasterData && this.zevMasterData.source_refs
        ? this.zevMasterData.source_refs[sectionKey] || {}
        : {};
      const refs = sourceMap[vehicleKey] && sourceMap[vehicleKey][fuelKey]
        ? sourceMap[vehicleKey][fuelKey]
        : [];

      if (!refs.length) {
        return 'Source metadata unavailable for this Master-sheet value.';
      }

      const sectionLabelMap = {
        sales_tax: 'Sales Tax',
        import_duty: 'Import Duty',
        subsidy: 'Subsidy',
      };

      return this.formatSourceTooltip([
        `${sectionLabelMap[sectionKey]} / ${this.getVehicleLabelByKey(vehicleKey)} / ${this.getFuelLabelByKey(fuelKey)}`,
        `Cells: ${refs.join(', ')}`,
      ]);
    },

    getEnergySourceText(sectionKey, variantKey, label) {
      const ref = this.zevMasterData && this.zevMasterData.source_refs
        ? (this.zevMasterData.source_refs[sectionKey] || {})[variantKey]
        : null;

      if (!ref || !ref.cell) {
        return 'Source metadata unavailable for this Master-sheet value.';
      }

      return this.formatSourceTooltip([
        `${label || ref.row_label}: ${ref.cell}`,
      ]);
    },

    getRenewableSectionSourceText() {
      return this.formatSourceTooltip([
        this.getEnergySourceText('renewable_generation_shares', 'baseline', 'Renewable generation shares / Baseline'),
        this.getEnergySourceText('renewable_generation_shares', 'scenario', 'Renewable generation shares / Scenario'),
      ]);
    },

    getCarbonSectionSourceText() {
      return this.formatSourceTooltip([
        this.getEnergySourceText('carbon_price', 'low', 'Environmental damage costs / CO2 / Low'),
        this.getEnergySourceText('carbon_price', 'high', 'Environmental damage costs / CO2 / High'),
      ]);
    },

    getCountryName(value) {
      if (!value) return '';
      const country = this.countries.find(c => c.name === value);
      return country ? country.name : value;
    },

    getCountryCode(countryName) {
      if (!countryName) return '';
      return COUNTRY_CODE_BY_NAME[countryName] || '';
    },

    parseTargetYearLabel(label) {
      const match = String(label || '').match(/(20\d{2})/);
      return match ? Number(match[1]) : null;
    },

    getSelectedTargetYearLabel() {
      const option = this.targetYears.find((item) => item.value === this.targetYear);
      return option ? option.text : `${this.targetYear}`;
    },

    getTaxRateMax(vehicleType) {
      return vehicleType === 'lcv' ? 300 : 250;
    },

    mapPolicyValuesToDecimals(values) {
      return Object.keys(values || {}).reduce((result, key) => {
        result[key] = values[key] / 100;
        return result;
      }, {});
    },

    getSubsidyMax(vehicleType) {
      return (vehicleType === 'bus' || vehicleType === 'hdv') ? 1000000 : 100000;
    },

    commitSliderValue(target, key, event, min, max) {
      const rawValue = String(event.target.value || '').replace(/[$,%\s]/g, '');
      const parsedValue = Number(rawValue);
      const currentValue = target ? target[key] : this[key];
      const nextValue = Number.isFinite(parsedValue)
        ? Math.min(max, Math.max(min, Math.round(parsedValue)))
        : currentValue;

      if (target) {
        this.$set(target, key, nextValue);
      } else {
        this[key] = nextValue;
      }

      event.target.value = nextValue;
    },

    getCompletionColor() {
      // Simple completion logic - can be enhanced
      return 'green';
    },

    getCompletionTextColor() {
      return 'white';
    },

    getCompletionIcon() {
      return 'mdi-check-circle';
    },

    startAnalysisProgress() {
      this.clearAnalysisProgressTimers();
      this.analysisElapsedSeconds = 0;
      this.analysisProgress.visible = true;
      this.analysisProgress.status = 'running';
      this.analysisProgress.message = '';
      this.analysisProgress.detail = '';
      this.analysisProgress.currentStepIndex = 0;

      this.analysisElapsedTimer = window.setInterval(() => {
        this.analysisElapsedSeconds += 1;
      }, 1000);

      this.analysisStepTimer = window.setInterval(() => {
        const nextIndex = Math.min(
          this.analysisProgress.steps.length - 1,
          this.analysisProgress.currentStepIndex + 1
        );
        this.analysisProgress.currentStepIndex = nextIndex;
      }, 12000);
    },

    waitForAnalysisUi(ms) {
      return new Promise((resolve) => {
        window.setTimeout(resolve, ms);
      });
    },

    async playDemoAnalysisProgress() {
      this.clearAnalysisProgressTimers();
      const lastRunningStepIndex = this.analysisProgress.steps.length - 1;
      while (this.analysisProgress.currentStepIndex < lastRunningStepIndex) {
        await this.waitForAnalysisUi(DEMO_ANALYSIS_STEP_DELAY_MS);
        this.analysisProgress.currentStepIndex += 1;
      }
    },

    finishAnalysisProgress() {
      this.analysisProgress.status = 'success';
      this.analysisProgress.currentStepIndex = this.analysisProgress.steps.length;
      this.clearAnalysisProgressTimers();
    },

    resetAnalysisProgress() {
      this.clearAnalysisProgressTimers();
      this.analysisProgress.visible = false;
      this.analysisProgress.status = 'idle';
      this.analysisProgress.message = '';
      this.analysisProgress.detail = '';
      this.analysisProgress.currentStepIndex = 0;
      this.analysisElapsedSeconds = 0;
    },

    closeAnalysisProgress() {
      if (this.isRunningAnalysis) return;
      this.resetAnalysisProgress();
    },

    clearAnalysisProgressTimers() {
      if (this.analysisElapsedTimer) {
        clearInterval(this.analysisElapsedTimer);
        this.analysisElapsedTimer = null;
      }
      if (this.analysisStepTimer) {
        clearInterval(this.analysisStepTimer);
        this.analysisStepTimer = null;
      }
    },

    analysisProgressStepClass(index) {
      if (index < this.analysisProgress.currentStepIndex) {
        return 'is-complete';
      }
      if (index === this.analysisProgress.currentStepIndex && this.isRunningAnalysis) {
        return 'is-active';
      }
      return 'is-pending';
    },

    formatAnalysisElapsed() {
      const minutes = Math.floor(this.analysisElapsedSeconds / 60);
      const seconds = this.analysisElapsedSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    },

    setAnalysisError(message, detail = '') {
      this.clearAnalysisProgressTimers();
      this.analysisProgress.visible = true;
      this.analysisProgress.status = 'error';
      this.analysisProgress.message = message;
      this.analysisProgress.detail = detail;
    },

    isOutputLocked(index) {
      return index >= 4 && !this.canAccessOutputs;
    },

    handleOutputLoadingState(payload) {
      if (!payload) return;
      if (this.analysisResult) {
        return;
      }
      if (payload.section && payload.isLoading && this.outputSectionsRead[payload.section]) {
        return;
      }
      if (payload.isLoading) {
        this.outputReadProgress.visible = true;
        this.outputReadProgress.section = payload.section || '';
        this.outputReadProgress.sectionTitle = payload.sectionTitle || 'Loading output data';
        this.outputReadProgress.fileName = payload.fileName || '';
        return;
      }

      if (this.outputReadProgress.section && payload.section && this.outputReadProgress.section !== payload.section) {
        return;
      }

      this.outputReadProgress.visible = false;
      this.outputReadProgress.section = '';
      this.outputReadProgress.sectionTitle = '';
      this.outputReadProgress.fileName = '';
    },

    handleOutputFileRead(payload) {
      if (!payload) return;
      if (payload.section) {
        this.$set(this.outputSectionsRead, payload.section, true);
      }
      console.log(`[Output Read] ${payload.sectionTitle || payload.section}: ${payload.fileName || 'latest analysis workbook'}`);
    },

    warmOutputCache(fileName) {
      if (!fileName) return;
      fetch(this.$domain + '/zev/warm_output_cache', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          file_name: fileName,
          sections: ['fact_chart', 'tco_graph', 'energy_chart', 'excel_graph']
        })
      }).catch((error) => {
        console.warn('Background output cache warm failed:', error);
      });
    },

    handleBeforeUnload(event) {
      if (!this.isRunningAnalysis) return undefined;
      const message = 'Analysis is still running. Leaving now will interrupt the current run.';
      event.preventDefault();
      event.returnValue = message;
      return message;
    },

    openGreenHydrogenModal(previousHydrogenShare = this.hydrogenShare) {
      this.greenHydrogenModal.visible = true
      this.greenHydrogenModal.value = this.greenHydrogenShare || this.scenarioConfigDefaults.green_hydrogen_share || '100% Green'
      this.greenHydrogenModal.previousHydrogenShare = previousHydrogenShare
    },

    confirmGreenHydrogenModal() {
      this.greenHydrogenShare = this.greenHydrogenModal.value || this.scenarioConfigDefaults.green_hydrogen_share || '100% Green'
      this.greenHydrogenShareConfirmed = true
      this.greenHydrogenModal.visible = false
    },

    cancelGreenHydrogenModal() {
      this.greenHydrogenModal.visible = false
      this.suppressGreenHydrogenModal = true
      if (!this.greenHydrogenModal.previousHydrogenShare || this.greenHydrogenModal.previousHydrogenShare === 'No Hydrogen') {
        this.includeFcev = false
      }
      this.hydrogenShare = this.greenHydrogenModal.previousHydrogenShare || 'No Hydrogen'
      this.$nextTick(() => {
        this.suppressGreenHydrogenModal = false
      })
    },

    getCompletionText() {
      return 'All Inputs Complete';
    },

    getAverageTaxRate(type) {
      const rates = this.taxRates[type] || {};
      const values = Object.values(rates);
      const average = values.reduce((sum, val) => sum + val, 0) / values.length;
      return Math.round(average);
    },

    getAverageDutyRate(type) {
      const rates = this.importDutyRates[type] || {};
      const values = Object.values(rates);
      const average = values.reduce((sum, val) => sum + val, 0) / values.length;
      return Math.round(average);
    },

    getTotalSubsidy(type) {
      const subsidies = this.subsidyAmounts[type] || {};
      const total = Object.values(subsidies).reduce((sum, val) => sum + val, 0);
      return total.toLocaleString();
    },

    getRenewableLabel() {
      if (this.renewablePercentage <= 40) return 'Low';
      if (this.renewablePercentage <= 70) return 'Medium';
      return 'High';
    },

    getCarbonPriceColor() {
      if (this.carbonPrice <= 50) return 'orange';
      if (this.carbonPrice <= 100) return 'blue-grey';
      return 'green';
    },

    getCarbonPriceLabel() {
      if (this.carbonPrice <= 50) return 'Low';
      if (this.carbonPrice <= 100) return 'Medium';
      return 'High';
    },

    convertToPercentage(value) {
      if (value === undefined || value === null) return 0;
      return Math.round(value * 100);
    },

    getDefaultVehiclePercentages() {
      const defaults = {
        car: 50,
        "2w": 75,
        "3w": 75,
        mb: 50,
        bus: 50,
        ab: 50,
        lcv: 50,
        hdv: 50,
      };

      if (!this.zevScenarioData || !this.zevScenarioData.vehicle_percentages) {
        return defaults;
      }

      const excelToVehicleMap = Object.keys(SCENARIO_VEHICLE_DEFINITIONS).reduce((mapping, header) => {
        mapping[header] = SCENARIO_VEHICLE_DEFINITIONS[header].value;
        return mapping;
      }, {});
      const values = { ...defaults };

      Object.keys(this.zevScenarioData.vehicle_percentages).forEach(excelHeader => {
        const vehicleKey = excelToVehicleMap[excelHeader];
        if (vehicleKey && Object.prototype.hasOwnProperty.call(values, vehicleKey)) {
          values[vehicleKey] = this.zevScenarioData.vehicle_percentages[excelHeader];
        }
      });

      return values;
    },

    getDefaultEnergyCarbonSettings() {
      const defaults = {
        renewablePercentage: 52,
        carbonPrice: 55,
      };
      const masterData = this.zevMasterData && this.zevMasterData.data;

      if (!masterData) return defaults;

      return {
        renewablePercentage: masterData.renewable_generation_shares
          ? Math.round(masterData.renewable_generation_shares.baseline * 100)
          : defaults.renewablePercentage,
        carbonPrice: masterData.carbon_price && masterData.carbon_price.low !== undefined
          ? Math.round(masterData.carbon_price.low)
          : defaults.carbonPrice,
      };
    },

    getDefaultTaxSettings() {
      const defaults = {
        taxRates: {
          petrol_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          diesel_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        },
        importDutyRates: {
          petrol_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          diesel_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        },
        subsidyAmounts: {
          bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
          hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        },
      };
      const masterData = this.zevMasterData && this.zevMasterData.data;

      if (!masterData) return defaults;

      if (masterData.sales_tax) {
        Object.keys(masterData.sales_tax).forEach(vehicleKey => {
          if (Object.prototype.hasOwnProperty.call(defaults.taxRates.petrol_ice, vehicleKey)) {
            const vehicleData = masterData.sales_tax[vehicleKey];
            ['petrol_ice', 'diesel_ice', 'bev', 'hydrogen'].forEach((fuelKey) => {
              if (vehicleData[fuelKey] !== undefined) {
                defaults.taxRates[fuelKey][vehicleKey] = this.convertToPercentage(vehicleData[fuelKey]);
              }
            });
          }
        });
      }

      if (masterData.import_duty) {
        Object.keys(masterData.import_duty).forEach(vehicleKey => {
          if (Object.prototype.hasOwnProperty.call(defaults.importDutyRates.petrol_ice, vehicleKey)) {
            const vehicleData = masterData.import_duty[vehicleKey];
            ['petrol_ice', 'diesel_ice', 'bev', 'hydrogen'].forEach((fuelKey) => {
              if (vehicleData[fuelKey] !== undefined) {
                defaults.importDutyRates[fuelKey][vehicleKey] = this.convertToPercentage(vehicleData[fuelKey]);
              }
            });
          }
        });
      }

      if (masterData.subsidy) {
        Object.keys(masterData.subsidy).forEach(vehicleKey => {
          if (Object.prototype.hasOwnProperty.call(defaults.subsidyAmounts.bev, vehicleKey)) {
            const vehicleData = masterData.subsidy[vehicleKey];
            if (vehicleData.bev !== undefined) {
              defaults.subsidyAmounts.bev[vehicleKey] = Math.round(vehicleData.bev);
            } else if (vehicleData.zev !== undefined) {
              defaults.subsidyAmounts.bev[vehicleKey] = Math.round(vehicleData.zev);
            }
            if (vehicleData.hydrogen !== undefined) {
              defaults.subsidyAmounts.hydrogen[vehicleKey] = Math.round(vehicleData.hydrogen);
            } else if (vehicleData.fcev !== undefined) {
              defaults.subsidyAmounts.hydrogen[vehicleKey] = Math.round(vehicleData.fcev);
            } else if (vehicleData.zev !== undefined) {
              defaults.subsidyAmounts.hydrogen[vehicleKey] = Math.round(vehicleData.zev);
            }
          }
        });
      }

      return defaults;
    },

    resetEvPercentageDefaults() {
      this.vehiclePercentages = this.getDefaultVehiclePercentages();
    },

    resetEnergyCarbonDefaults() {
      const defaults = this.getDefaultEnergyCarbonSettings();
      this.renewablePercentage = defaults.renewablePercentage;
      this.carbonPrice = defaults.carbonPrice;
    },

    resetActiveTaxTabDefaults() {
      const defaults = this.getDefaultTaxSettings();

      if (this.activeTab === 0) {
        this.taxRates = JSON.parse(JSON.stringify(defaults.taxRates));
      } else if (this.activeTab === 1) {
        this.importDutyRates = JSON.parse(JSON.stringify(defaults.importDutyRates));
      } else if (this.activeTab === 2) {
        this.subsidyAmounts = JSON.parse(JSON.stringify(defaults.subsidyAmounts));
      }
    },

    exportSummary() {
      // TODO: Implement export functionality
      console.log('Export summary');
    },

    saveSummary() {
      // TODO: Implement save functionality
      console.log('Save summary');
    },

    async fetchZevConfigData() {
      try {
        this.isBootstrappingConfig = true;
        let response = await fetch(this.$domain + '/zev/get_config_data', {
          method: 'GET'
        });

        if (!response.ok && (response.status === 404 || response.status === 405)) {
          response = await fetch(this.$domain + '/zev/get_config_data', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({})
          });
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        if (data.error) {
          throw new Error(data.error);
        }

        const workbookCountries = Array.isArray(data.countries) ? data.countries : [];
        this.countries = workbookCountries.map((name) => ({
          name,
          code: this.getCountryCode(name)
        }));

        if (data.options) {
          this.hydrogenShareOptions = data.options.hydrogen_share || [];
          this.powerGenerationMixOptions = data.options.power_generation_mix || [];
          this.greenHydrogenShareOptions = data.options.green_hydrogen_share || [];

          const targetYearOptions = Array.isArray(data.options.target_year) ? data.options.target_year : [];
          if (targetYearOptions.length) {
            this.targetYears = targetYearOptions
              .map((label) => {
                const parsedYear = this.parseTargetYearLabel(label);
                return parsedYear ? { text: `by ${parsedYear}`, value: parsedYear } : null;
              })
              .filter(Boolean);
          }
        }

        if (data.defaults) {
          this.scenarioConfigDefaults = {
            ...this.scenarioConfigDefaults,
            ...data.defaults,
          };
        }

        const defaultCountry = this.scenarioConfigDefaults.country_name;
        const defaultYear = this.parseTargetYearLabel(this.scenarioConfigDefaults.target_year_label);

        this.selectedCountry = (defaultCountry && this.countries.some(country => country.name === defaultCountry))
            ? defaultCountry
            : (this.countries[0] ? this.countries[0].name : this.selectedCountry);
        this.targetYear = defaultYear || this.targetYear;
        this.hydrogenShare = this.scenarioConfigDefaults.hydrogen_share || this.hydrogenShare;
        this.includeFcev = this.hydrogenShare !== 'No Hydrogen';
        this.powerGenerationMix = this.scenarioConfigDefaults.power_generation_mix || this.powerGenerationMix;
        this.greenHydrogenShare = this.scenarioConfigDefaults.green_hydrogen_share || this.greenHydrogenShare;
        this.greenHydrogenShareConfirmed = this.hydrogenShare !== 'No Hydrogen' && !!this.greenHydrogenShare;
        } catch (error) {
          console.error('Error fetching ZEV config data:', error);
        } finally {
        this.isBootstrappingConfig = false;
      }
    },

    async fetchZevScenarioData() {
      try {
        this.isLoadingScenarioData = true;
        
        console.log('Starting scenario data fetch...');
        
        const response = await fetch(this.$domain + '/zev/get_scenario_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            target_year: this.targetYear,
            country_name: this.getCountryName(this.selectedCountry)
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        console.log('ZEV scenario response:', data);
        
        if (data.error) {
          throw new Error(data.error);
        }

        this.zevScenarioData = data;
        
        // Update vehicle percentages from Excel data using dynamic mapping
        if (data.vehicle_percentages) {
          // Create mapping from Excel headers to our internal vehicle keys
          const excelToVehicleMap = Object.keys(SCENARIO_VEHICLE_DEFINITIONS).reduce((mapping, header) => {
            mapping[header] = SCENARIO_VEHICLE_DEFINITIONS[header].value;
            return mapping;
          }, {});

          // Create new object to ensure Vue reactivity
          const newVehiclePercentages = { ...this.vehiclePercentages };

          // Update percentages based on Excel column names
          Object.keys(data.vehicle_percentages).forEach(excelHeader => {
            const vehicleKey = excelToVehicleMap[excelHeader];
            if (vehicleKey && Object.prototype.hasOwnProperty.call(newVehiclePercentages, vehicleKey)) {
              newVehiclePercentages[vehicleKey] = data.vehicle_percentages[excelHeader];
            }
          });

          // Update the reactive object
          this.vehiclePercentages = newVehiclePercentages;
        }
        
        console.log('ZEV Scenario Data loaded successfully');
        
      } catch (error) {
        console.error('Error fetching ZEV scenario data:', error);
        this.zevScenarioData = null;
        // Show error to user
        this.$nextTick(() => {
          alert(`Error loading scenario data: ${error.message}`);
        });
      } finally {
        this.isLoadingScenarioData = false;
        console.log('Scenario data fetch completed');
      }
    },

    async fetchZevMasterData() {
      try {
        this.isLoadingMasterData = true;
        
        console.log('Starting master data fetch...');
        
        const response = await fetch(this.$domain + '/zev/get_master_data', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            country_name: this.getCountryName(this.selectedCountry),
            target_year: this.targetYear
          })
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        
        if (data.error) {
          throw new Error(data.error);
        }

        this.zevMasterData = data;
        
        // Helper function to convert decimal to percentage
        const convertToPercentage = (value) => {
          if (value === undefined || value === null) return 0;
          return Math.round(value * 100);
        };
        
        // Update data from Master tab
        if (data.data) {
          // Update Energy & Carbon data - simplified
          const energyData = data.data
          console.log('Energy Data:', energyData);
          this.renewablePercentage = Math.round(energyData.renewable_generation_shares.baseline * 100);
                      
            // Set carbon price from low value or fallback
            this.carbonPrice = Math.round(energyData.carbon_price.low);
        
          
          // Update Sales Tax data
          if (data.data.sales_tax) {
            const newTaxRates = JSON.parse(JSON.stringify(this.taxRates));
            
            Object.keys(data.data.sales_tax).forEach(vehicleKey => {
              if (Object.prototype.hasOwnProperty.call(newTaxRates.petrol_ice, vehicleKey)) {
                const vehicleData = data.data.sales_tax[vehicleKey];
                ['petrol_ice', 'diesel_ice', 'bev', 'hydrogen'].forEach((fuelKey) => {
                  if (vehicleData[fuelKey] !== undefined) {
                    newTaxRates[fuelKey][vehicleKey] = convertToPercentage(vehicleData[fuelKey]);
                  }
                });
              }
            });
            
            this.taxRates = newTaxRates;
          }
          
          // Update Import Duty data
          if (data.data.import_duty) {
            const newImportDutyRates = JSON.parse(JSON.stringify(this.importDutyRates));
            
            Object.keys(data.data.import_duty).forEach(vehicleKey => {
              if (Object.prototype.hasOwnProperty.call(newImportDutyRates.petrol_ice, vehicleKey)) {
                const vehicleData = data.data.import_duty[vehicleKey];
                ['petrol_ice', 'diesel_ice', 'bev', 'hydrogen'].forEach((fuelKey) => {
                  if (vehicleData[fuelKey] !== undefined) {
                    newImportDutyRates[fuelKey][vehicleKey] = convertToPercentage(vehicleData[fuelKey]);
                  }
                });
              }
            });
            
            this.importDutyRates = newImportDutyRates;
          }
          
          // Update Subsidy data
          if (data.data.subsidy) {
            const newSubsidyAmounts = JSON.parse(JSON.stringify(this.subsidyAmounts));
            
            Object.keys(data.data.subsidy).forEach(vehicleKey => {
              if (Object.prototype.hasOwnProperty.call(newSubsidyAmounts.bev, vehicleKey)) {
                const vehicleData = data.data.subsidy[vehicleKey];
                
                if (vehicleData.bev !== undefined) {
                  newSubsidyAmounts.bev[vehicleKey] = Math.round(vehicleData.bev);
                } else if (vehicleData.zev !== undefined) {
                  newSubsidyAmounts.bev[vehicleKey] = Math.round(vehicleData.zev);
                }
                if (vehicleData.hydrogen !== undefined) {
                  newSubsidyAmounts.hydrogen[vehicleKey] = Math.round(vehicleData.hydrogen);
                } else if (vehicleData.fcev !== undefined) {
                  newSubsidyAmounts.hydrogen[vehicleKey] = Math.round(vehicleData.fcev);
                } else if (vehicleData.zev !== undefined) {
                  newSubsidyAmounts.hydrogen[vehicleKey] = Math.round(vehicleData.zev);
                }
              }
            });
            
            this.subsidyAmounts = newSubsidyAmounts;
          }
        }
        
        console.log('ZEV Master Data loaded successfully');
        
      } catch (error) {
        console.error('Error fetching ZEV master data:', error);
        this.zevMasterData = null;
        console.warn(`Could not load master data: ${error.message}`);
      } finally {
        this.isLoadingMasterData = false;
        console.log('Master data fetch completed');
      }
    },

    // Add a combined method to fetch both data sources
    async fetchBothData() {
      if (!this.targetYear || !this.selectedCountry) return;
      
      console.log('Starting to fetch both data sources...');
      
      // Fetch both data sources in parallel to improve performance
      await Promise.all([
        this.fetchZevScenarioData(),
        this.fetchZevMasterData()
      ]);
      
      console.log('All data fetching completed');
    },

    // Add method to gather all summary data
    gatherSummaryData() {
      // Generate timestamp in yyyy-mm-dd_hh.mm.ss format
      const now = new Date();
      const timestamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}_${String(now.getHours()).padStart(2, '0')}.${String(now.getMinutes()).padStart(2, '0')}.${String(now.getSeconds()).padStart(2, '0')}`;
      
      const hydrogenAbbrev = String(this.hydrogenShare || 'No Hydrogen').replace(/[^A-Za-z0-9]+/g, '');
      const powerMixAbbrev = String(this.powerGenerationMix || 'CPAT Projection').replace(/[^A-Za-z0-9]+/g, '');
      
      // Create unique ID
      const summaryId = `${this.getCountryCode(this.selectedCountry) || this.selectedCountry}_${hydrogenAbbrev}_${powerMixAbbrev}_${this.targetYear}_${timestamp}`;
      
      // Create comprehensive summary object with all scenario data
      const summaryData = {
        // Unique ID
        id: summaryId,
        
        // Country and configuration info
        country: {
          code: this.getCountryCode(this.selectedCountry) || this.selectedCountry,
          name: this.getCountryName(this.selectedCountry)
        },
        target_year: this.targetYear,
        scenario_inputs: {
          target_year_label: this.getSelectedTargetYearLabel(),
          hydrogen_share: this.hydrogenShare,
          power_generation_mix: this.powerGenerationMix,
          green_hydrogen_share: this.showGreenHydrogenShare ? this.greenHydrogenShare : 'No Hydrogen',
        },
        
        // ZEV Percentage section - convert to decimals
        zev_percentage: {
          car: this.vehiclePercentages.car / 100,
          '2w': this.vehiclePercentages['2w'] / 100,
          '3w': this.vehiclePercentages['3w'] / 100,
          mb: this.vehiclePercentages.mb / 100,
          bus: this.vehiclePercentages.bus / 100,
          ab: this.vehiclePercentages.ab / 100,
          lcv: this.vehiclePercentages.lcv / 100,
          hdv: this.vehiclePercentages.hdv / 100
        },
        
        // Financials section - convert percentages to decimals
        financials: {
          tax: {
            petrol_ice: this.mapPolicyValuesToDecimals(this.taxRates.petrol_ice),
            diesel_ice: this.mapPolicyValuesToDecimals(this.taxRates.diesel_ice),
            bev: this.mapPolicyValuesToDecimals(this.taxRates.bev),
            hydrogen: this.mapPolicyValuesToDecimals(this.taxRates.hydrogen),
          },
          import_duty: {
            petrol_ice: this.mapPolicyValuesToDecimals(this.importDutyRates.petrol_ice),
            diesel_ice: this.mapPolicyValuesToDecimals(this.importDutyRates.diesel_ice),
            bev: this.mapPolicyValuesToDecimals(this.importDutyRates.bev),
            hydrogen: this.mapPolicyValuesToDecimals(this.importDutyRates.hydrogen),
          },
          subsidy: {
            bev: { ...this.subsidyAmounts.bev },
            hydrogen: { ...this.subsidyAmounts.hydrogen }
          }
        },
        
        // Energy & Carbon section - convert renewable percentage to decimal
        energy_n_carbon: {
          renewable_energy: {
            percentage: this.renewablePercentage / 100,
            label: this.getRenewableLabel()
          },
          carbon_price: {
            value: this.carbonPrice,
            label: this.getCarbonPriceLabel(),
            unit: 'USD per ton CO₂'
          }
        },
        
        // Timestamp
        generated_at: now.toISOString()
      };
      
      return summaryData;
    },

    async runAnalysis() {
      if (!this.isAnalysisReady) {
        alert("Please ensure all inputs are complete before running analysis.");
        return;
      }

      try {
        // Show loading state using dedicated property
        this.isRunningAnalysis = true;
        this.startAnalysisProgress();
        
        // Gather all summary data
        const summaryData = this.gatherSummaryData();
        this.analysisProgress.currentStepIndex = 1;
        
        console.log('Running analysis with summary data:', summaryData);
        
        // Send summaryData to backend API for analysis
        const response = await fetch(this.$domain + '/zev/run_analysis', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(summaryData)
        });
        const analysisResult = await response.json();
        if (!response.ok) {
          throw new Error(analysisResult.error || analysisResult.detail || `HTTP error! status: ${response.status}`);
        }
        
        if (analysisResult.error) {
          throw new Error(analysisResult.error);
        }
        
        console.log('Analysis result:', analysisResult);

        if (analysisResult.demo_mode) {
          this.analysisProgress.detail = `Demo mode is enabled. This run is simulating the analysis workflow for ${analysisResult.file_name}.`;
          await this.playDemoAnalysisProgress();
        } else {
          this.analysisProgress.currentStepIndex = this.analysisProgress.steps.length - 1;
        }
        
        // Store the analysis result
        this.analysisResult = analysisResult;
        const preloadedSections = analysisResult.preloaded_outputs
          ? Object.keys(analysisResult.preloaded_outputs)
          : [];
        preloadedSections.forEach((section) => {
          this.$set(this.outputSectionsRead, section, true);
        });
        if (analysisResult.demo_mode) {
          this.analysisProgress.message = `Demo analysis completed for ${analysisResult.country.name}. You can close this window now.`;
          this.analysisProgress.detail = `No Excel calculation was run. Output tabs will read data from ${analysisResult.file_name} when opened.`;
        } else {
          this.analysisProgress.message = `Analysis completed successfully for ${analysisResult.country.name}. You can close this window now.`;
          this.analysisProgress.detail = 'Output tabs will load on demand. Core output caches are warming in the background.';
        }
        this.finishAnalysisProgress();
        if (!analysisResult.demo_mode) {
          this.warmOutputCache(analysisResult.file_name);
        }
        
      } catch (error) {
        console.error('Error running analysis:', error);
        this.setAnalysisError('The analysis could not be completed.', error.message);
      } finally {
        this.isRunningAnalysis = false;
      }
    },

    onRenewableSliderChange() {
      // Clear preset selection when slider is moved manually
      this.selectedRenewablePreset = null;
      console.log('Renewable slider changed, preset cleared');
    },

    onCarbonSliderChange() {
      // Clear preset selection when slider is moved manually
      this.selectedCarbonPreset = null;
      console.log('Carbon slider changed, preset cleared');
    },

    setSelectedScenario(index) {
      if (this.isOutputLocked(index)) {
        console.warn('Output tab blocked until analysis has been run.');
        return;
      }
      // Always set to the clicked index, preventing deselection
      this.selectedScenario = index;
      console.log("Selected scenario:", index); // Debug log
    },

      downloadResult() {
        if (!this.analysisResult || !this.analysisResult.file_name) {
          console.warn('No analysis workbook is available for download yet.');
          return;
        }

        const fileName = this.analysisResult.file_name;
        const url = this.analysisResult.demo_mode
          ? `${this.$domain}/static/model/${encodeURIComponent(fileName)}`
          : `${this.$domain}/static/model/analysis_results/${encodeURIComponent(fileName)}`;
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        console.log('Result workbook download started:', fileName);
      },

    resetScenarios() {
      // Implement reset functionality
        this.selectedCountry = this.scenarioConfigDefaults.country_name || (this.countries[0] ? this.countries[0].name : this.selectedCountry);
        this.targetYear = this.parseTargetYearLabel(this.scenarioConfigDefaults.target_year_label) || 2035;
        this.hydrogenShare = this.scenarioConfigDefaults.hydrogen_share || 'No Hydrogen';
        this.includeFcev = this.hydrogenShare !== 'No Hydrogen';
        this.powerGenerationMix = this.scenarioConfigDefaults.power_generation_mix || 'CPAT Projection';
        this.greenHydrogenShare = this.scenarioConfigDefaults.green_hydrogen_share || '100% Green';
        this.greenHydrogenShareConfirmed = this.hydrogenShare !== 'No Hydrogen' && !!this.greenHydrogenShare;
        this.vehiclePercentages = {
        car: 50,
        "2w": 75,
        "3w": 75,
        mb: 50,
        bus: 50,
        ab: 50,
        lcv: 50,
        hdv: 50,
      };
      this.taxRates = {
        petrol_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        diesel_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
      };
      this.importDutyRates = {
        petrol_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        diesel_ice: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
      };
      this.subsidyAmounts = {
        bev: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
        hydrogen: { car: 0, bus: 0, "2w": 0, "3w": 0, lcv: 0, hdv: 0 },
      };
      this.renewablePercentage = 52;
      this.carbonPrice = 55;
      this.selectedScenario = 0;
      this.activeTab = 0;
    },

    getCircularSize() {
      // Calculate size based on available space
      if (window.innerWidth < 768) return 24;
      if (window.innerWidth < 900) return 28;
      return 32;
    },

    scheduleZevDataReload() {
      clearTimeout(this.countryChangeTimeout);
      this.countryChangeTimeout = setTimeout(() => {
        this.fetchBothData();
      }, 200);
    },

    // Add explicit event handlers for dropdown changes
    onCountryChange(newCountry) {
      console.log('Country change event:', newCountry);
      if (this.isBootstrappingConfig) return;
      
      const isValidCountry = this.countries.some(country => country.name === newCountry);
      
      if (newCountry && this.targetYear && isValidCountry) {
        console.log('Valid country selection detected, fetching data...');
        this.scheduleZevDataReload();
      } else {
        console.log('Invalid country or missing requirements:', { 
          newCountry, 
          targetYear: this.targetYear, 
          isValidCountry 
        });
      }
    },

    onYearChange(newYear) {
      console.log('Year input event:', newYear);
      if (this.isBootstrappingConfig) return;
      
      // Only proceed if the new value is a valid year from our list
      const isValidYear = this.targetYears.some(year => year.value === newYear);
      
      if (newYear && this.selectedCountry && isValidYear) {
        console.log('Valid year selection detected, fetching data...');
        this.scheduleZevDataReload();
      } else {
        console.log('Invalid year or missing requirements:', { 
          newYear, 
          selectedCountry: this.selectedCountry, 
                   isValidYear 
        });
      }
    },
  },

  watch: {
    selectedCountry(newCountry, oldCountry) {
      if (newCountry !== oldCountry) {
        this.onCountryChange(newCountry);
      }
    },

    targetYear(newYear, oldYear) {
      if (newYear !== oldYear) {
        this.onYearChange(newYear);
      }
    },

    includeFcev(newValue, oldValue) {
      if (newValue === oldValue) return;
      if (this.isBootstrappingConfig) return;

      if (!newValue) {
        this.hydrogenShare = 'No Hydrogen';
        return;
      }
    },

    hydrogenShare(newValue, oldValue) {
        if (newValue === oldValue) return;
        if (this.isBootstrappingConfig) return;
        if (this.suppressGreenHydrogenModal) return;
        if (newValue === 'No Hydrogen') {
          this.includeFcev = false;
          this.greenHydrogenShare = this.scenarioConfigDefaults.green_hydrogen_share || '100% Green';
          this.greenHydrogenShareConfirmed = false;
          return;
        }
        if (newValue !== 'No Hydrogen') {
          this.includeFcev = true;
          this.openGreenHydrogenModal(oldValue);
        }
      },

  },

  mounted() {
    window.addEventListener('beforeunload', this.handleBeforeUnload);
    this.fetchZevConfigData().then(() => {
      if (this.selectedCountry && this.targetYear) {
        this.fetchBothData();
      }
    });
  },

  beforeRouteLeave(to, from, next) {
    if (!this.isRunningAnalysis) {
      next();
      return;
    }

    const shouldLeave = window.confirm('Analysis is still running. Leave this page and interrupt the current run?');
    next(shouldLeave);
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.handleBeforeUnload);
    this.clearAnalysisProgressTimers();
    // Clean up timeout if component is destroyed
    if (this.countryChangeTimeout) {
      clearTimeout(this.countryChangeTimeout);
    }
  },
};
</script>

<style>
@import '../static/css/tools.css';
</style>
